import { useEffect, useRef, useState, useCallback } from "react";
import isEqual from "lodash/isEqual";
import { getNodesTree } from "../services/hierarchy";

const IGNORE_FIELDS = ["realtime"];

function normalizeTree(nodes, ignoreFields = []) {
    const strip = (obj) => {
        if (!obj || typeof obj !== "object") return obj;
        if (Array.isArray(obj)) return obj.map(strip);

        const out = {};
        for (const k of Object.keys(obj)) {
            if (ignoreFields.includes(k)) continue;
            out[k] = strip(obj[k]);
        }
        return out;
    };

    const normalizeNode = (n) => {
        const base = strip(n);

        const hijos = Array.isArray(base?.hijos) ? base.hijos : [];
        const tags = Array.isArray(base?.tags) ? base.tags : [];

        const hijosSorted = hijos
            .map(normalizeNode)
            .sort((a, b) => (a?.id ?? 0) - (b?.id ?? 0));

        const tagsSorted = [...tags].sort((a, b) => (a?.id ?? 0) - (b?.id ?? 0));

        return { ...base, hijos: hijosSorted, tags: tagsSorted };
    };

    return (nodes || [])
        .map(normalizeNode)
        .sort((a, b) => (a?.id ?? 0) - (b?.id ?? 0));
}

async function fetchNodesTree() {
    let res = await getNodesTree();
    return res.data;
}

// --- hook ---
export function useNodesTree() {
    const [tree, setTree] = useState([]);
    const [loading, setLoading] = useState(true);

    const aliveRef = useRef(true);
    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
        };
    }, []);

    const syncSilently = useCallback(async () => {
        try {
            const remoteRaw = await fetchNodesTree();
            const remoteNorm = normalizeTree(remoteRaw, IGNORE_FIELDS);

            if (!aliveRef.current) return;

            setTree((currentTree) => {
                const currentNorm = normalizeTree(currentTree, IGNORE_FIELDS);
                return isEqual(currentNorm, remoteNorm) ? currentTree : remoteRaw;
            });

        } catch (error) {
           console.error("Error fetching nodes tree:", error);
        }
    }, []);

    const updateTree = useCallback(() => {
        syncSilently();
    }, [syncSilently]);

    const replaceTree = useCallback((nextTree) => {
        setTree(nextTree);
    }, []);

    useEffect(() => {
        let isCancelled = false;

        const loadTree = async () => {
            try {
                await syncSilently();
            } finally {
                if (!isCancelled && aliveRef.current) {
                    setLoading(false);
                }
            }
        };

        loadTree();

        return () => {
            isCancelled = true;
        };
    }, [syncSilently]);

    return { tree, loading, updateTree, replaceTree };
}
